/*
 * Copyright (c) 2023 - 2026. Bluemate B.V.
 */

import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import ReviewAnalytics from './reviewAnalytics';
import {AccessKey, ReviewChartData, ReviewStatsDisplay, ReviewTab} from '../../util/interfaces';
import {onSetCrumbs} from '../../util/events';
import {ResponseData} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import {AppContext} from '../misc/appContext';
import {sendGaPageView} from '../../util/analytics';
import useRight from '../misc/useRight';
import {getReviewChartData, getReviewTotals} from '../../util/requestSender';
import {getReviewAnalyticsMemValue, setReviewAnalyticsMemValue} from '../../util/reviewAnalyticsUtil';
import {useTranslation} from 'react-i18next';
import {useBreakpoint} from '../../util/useBreakpoint';
import {showsMobileReviewFeed} from '../../util/accessUtil';

export default function ReviewAnalyticsController() {
    const {t} = useTranslation();
    const [accessProducts, accessFeed, accessExport] = useRight(
        AccessKey.PAGE_REVIEWS_PRODUCTS,
        AccessKey.PAGE_REVIEWS_FEED,
        AccessKey.PAGE_REVIEWS_EXPORT
    );
    const context = useContext(AppContext);
    const bp = useBreakpoint();

    const allowedTabs = useMemo(() => {
        const allowed: Array<ReviewTab> = [];
        if (accessFeed) allowed.push(ReviewTab.FEED);
        if (accessProducts) allowed.push(ReviewTab.PRODUCTS);
        if (accessExport) allowed.push(ReviewTab.EXPORT);
        return allowed;
    }, [accessProducts, accessFeed, accessExport]);
    // On the bottom-nav breakpoint range the page reduces to the simplified
    // card feed (no chart/stats/tabs). Users without feed access keep the
    // regular layout, also on mobile — there is nothing else to show them.
    // Shared with app.tsx's fluid-width gate so the two can never disagree.
    const showMobileFeed = showsMobileReviewFeed(context.accountContext.account, bp.isMobileLayout);
    const defaultTab = useMemo(() => {
        return allowedTabs[0];
    }, [allowedTabs]);

    // getReviewDatesFromPeriod has no CUSTOM branch (it would return a 0-day
    // "today..today" window) — a remembered custom period must restore its
    // stored dates instead. This effect re-runs when the layout crosses back
    // into desktop (showMobileFeed dep below), so wrong defaults here would
    // clobber an active custom range on a mere resize/rotation.
    const [tab, setTab] = useState<ReviewTab>(getTabFromParams());
    const [loadingChart, setLoadingChart] = useState(true);
    const [loadingCountStats, setLoadingCountStats] = useState(true);
    const [chartData, setChartData] = useState<Record<string, ReviewChartData>>({});
    const [totalPositive, setTotalPositive] = useState(0);
    const [totalNegative, setTotalNegative] = useState(0);
    const [totalPositiveIncrease, setTotalPositiveIncrease] = useState(0);
    const [totalNegativeIncrease, setTotalNegativeIncrease] = useState(0);
    const [npsScore, setNpsScore] = useState(0);
    const [periodNpsScore, setPeriodNpsScore] = useState(0);
    const [statsDisplay, setStatsDisplay] = useState<ReviewStatsDisplay>(ReviewStatsDisplay.STATS_ONLY);

    // Display dates, fixed to the past 30 days.
    // These match the backend which always uses 30 days in period comparisons.
    const fromDate = useMemo(() => new IDate().subtract(30, 'days'), []);
    const toDate = useMemo(() => new IDate(), []);

    // Latest-request ids for the chart/totals loads — a slower, superseded
    // response (e.g. the breakpoint-crossing effect racing a period change)
    // must not overwrite the dates/stats of a newer request.
    const chartRequestSeq = useRef(0);
    const totalsRequestSeq = useRef(0);
    // Account the chart/totals are loaded for — the layout effect below skips
    // redundant reloads when crossing breakpoints with current data on screen.
    const loadedStatsForAccount = useRef<string | null>(null);

    function getTabFromParams(): ReviewTab {
        let _tab: ReviewTab = getReviewAnalyticsMemValue('tab') ?? defaultTab;
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.has('tab')) {
            const tab = searchParams.get('tab')!;
            if (tab === 'feed' && allowedTabs.includes(ReviewTab.FEED)) _tab = ReviewTab.FEED;
            else if (tab === 'settings' && allowedTabs.includes(ReviewTab.SETTINGS)) _tab = ReviewTab.SETTINGS;
        }
        setReviewAnalyticsMemValue('tab', _tab);
        return _tab;
    }

    function onClickStat(stat: 'all' | 'positive' | 'negative') {
        return function () {
            if (tab !== ReviewTab.FEED) onChangeTab(ReviewTab.FEED);
            // Wait for feed tab to display
            window.setTimeout(() => {
                document.dispatchEvent(new CustomEvent('reviewStatClick', {detail: stat}));
            }, 1);
        };
    }

    function onChangeStatsDisplay() {
        if (statsDisplay === ReviewStatsDisplay.FULL) setStatsDisplay(ReviewStatsDisplay.STATS_ONLY);
        else setStatsDisplay(ReviewStatsDisplay.FULL);
    }

    function onChangeTab(_tab: ReviewTab) {
        setReviewAnalyticsMemValue('tab', _tab);
        setTab(_tab);
    }

    function loadReviewTotals() {
        const {account} = context.accountContext;
        if (!account) return;
        setLoadingCountStats(true);
        totalsRequestSeq.current += 1;
        const seq = totalsRequestSeq.current;
        getReviewTotals({
            data: {account_id: account.id},
            callback: (data: ResponseData) => {
                if (seq !== totalsRequestSeq.current) return; // Superseded by a newer load
                setLoadingCountStats(false);
                if (data.success) {
                    const {
                        total_negative,
                        total_positive,
                        total_negative_increase,
                        total_positive_increase,
                        nps_score,
                        period_nps_score
                    } = data;
                    setTotalNegative(total_negative);
                    setTotalPositive(total_positive);
                    setTotalNegativeIncrease(total_negative_increase);
                    setTotalPositiveIncrease(total_positive_increase);
                    setNpsScore(nps_score);
                    setPeriodNpsScore(period_nps_score);
                }
            }
        });
    }

    function loadChartData() {
        const {account} = context.accountContext;
        if (!account) return;
        setLoadingChart(true);
        chartRequestSeq.current += 1;
        const seq = chartRequestSeq.current;
        getReviewChartData({
            data: {account_id: account.id},
            callback: (data: ResponseData) => {
                if (seq !== chartRequestSeq.current) return; // Superseded by a newer load
                setLoadingChart(false);
                if (data.success) setChartData(data.chart_data);
            }
        });
    }

    useEffect(() => {
        // The simplified mobile feed shows neither the chart nor the stat
        // cards — skip their requests there. Crossing the breakpoint into the
        // regular layout (e.g. tablet rotation) re-runs this effect and loads,
        // but only ONCE per account: period changes go through their own
        // handlers, so data already on screen is current and a mere layout
        // round-trip must not refetch it.
        const accountId = context.accountContext.account?.id ?? null;
        if (!showMobileFeed && accountId && loadedStatsForAccount.current !== accountId) {
            loadedStatsForAccount.current = accountId;
            loadChartData();
            loadReviewTotals();
        }
        if (!allowedTabs.includes(tab)) setTab(allowedTabs[0]);
    }, [context.accountContext.account, showMobileFeed]);

    useEffect(() => {
        sendGaPageView('/review-analytics');
        onSetCrumbs([{label: t('reviewAnalytics.crumb', 'Review Inzicht'), route: '/review-analytics'}]);
    }, []);

    return (
        <>
            <ReviewAnalytics
                showMobileFeed={showMobileFeed}
                onChangeTab={onChangeTab}
                onClickStat={onClickStat}
                onChangeStatsDisplay={onChangeStatsDisplay}
                tab={tab}
                toDate={toDate}
                fromDate={fromDate}
                allowedTabs={allowedTabs}
                chartData={chartData}
                loadingChart={loadingChart}
                loadingStats={loadingCountStats}
                totalNegative={totalNegative}
                totalNegativeIncrease={totalNegativeIncrease}
                totalPositive={totalPositive}
                totalPositiveIncrease={totalPositiveIncrease}
                statsDisplay={statsDisplay}
                npsScore={npsScore}
                periodNpsScore={periodNpsScore}
            />
        </>
    );
}
